import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OccEndpointsService } from '@spartacus/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  CreateOrUpdateOrderCardConfiguration,
  CreateOrderCard,
  OrderCard,
  OrderCardAdminSearchParams,
  OrderCardConfiguration,
  OrderCardPage,
  OrderCardStatus,
  UpdateOrderCardActionType,
} from '../../model';
import { Occ } from '../../occ/occ-models';
import { ConverterService } from '../../util/converter.service';
import { ORDER_CARDS_NORMALIZER, ORDER_CARD_CONFIGURATION_NORMALIZER, ORDER_CARD_NORMALIZER } from './converters';

@Injectable({
  providedIn: 'root',
})
export class OrderCardsAdminConnector {
  constructor(
    private httpClient: HttpClient,
    private occEndpointsService: OccEndpointsService,
    private converterService: ConverterService
  ) {}

  getOrderCard(userId: string, code: string): Observable<OrderCard> {
    const url = this.occEndpointsService.buildUrl('adminOrderCard', { urlParams: { userId, code } });
    return this.httpClient.get<Occ.OrderCard>(url).pipe(this.converterService.pipeable(ORDER_CARD_NORMALIZER));
  }

  getOrderCards(userId: string, searchParams: OrderCardAdminSearchParams): Observable<OrderCardPage> {
    const url = this.occEndpointsService.buildUrl('adminOrderCards', { urlParams: { userId }, queryParams: { ...searchParams } });
    return this.httpClient.get<Occ.OrderCardPage>(url).pipe(this.converterService.pipeable(ORDER_CARDS_NORMALIZER));
  }

  createOrderCard(userId: string, orderCardParams: CreateOrderCard): Observable<OrderCard> {
    const url = this.occEndpointsService.buildUrl('adminOrderCards', {
      urlParams: { userId },
      queryParams: { name: orderCardParams.name },
    });
    return this.httpClient.post<Occ.OrderCard>(url, {}).pipe(this.converterService.pipeable(ORDER_CARD_NORMALIZER));
  }

  updateOrderCard(
    userId: string,
    code: string,
    params: { name?: string; description?: string; contactPerson?: string }
  ): Observable<OrderCard> {
    const url = this.occEndpointsService.buildUrl('adminOrderCard', {
      urlParams: { userId, code },
      queryParams: { ...params },
    });
    return this.httpClient.patch<Occ.OrderCard>(url, {}).pipe(this.converterService.pipeable(ORDER_CARD_NORMALIZER));
  }

  updateOrderCardAdminStatus(userId: string, code: string, status: OrderCardStatus): Observable<OrderCard> {
    const url = this.occEndpointsService.buildUrl('updateAdminOrderCardStatus', {
      urlParams: { userId, code },
      queryParams: { status },
    });
    return this.httpClient.put<Occ.OrderCard>(url, null).pipe(this.converterService.pipeable(ORDER_CARD_NORMALIZER));
  }

  addOrderCardPrincipal(userId: string, code: string, principal: string): Observable<OrderCard> {
    const url = this.occEndpointsService.buildUrl('addOrDeleteAdminOrderCardPrincipal', {
      urlParams: { userId, code },
      queryParams: { principal: principal },
    });
    return this.httpClient.put<Occ.OrderCard>(url, {}).pipe(this.converterService.pipeable(ORDER_CARD_NORMALIZER));
  }

  deleteOrderCardPrincipal(userId: string, code: string, principal: string): Observable<OrderCard> {
    const url = this.occEndpointsService.buildUrl('addOrDeleteAdminOrderCardPrincipal', {
      urlParams: { userId, code },
      queryParams: { principal: principal },
    });
    return this.httpClient.delete<Occ.OrderCard>(url, {}).pipe(this.converterService.pipeable(ORDER_CARD_NORMALIZER));
  }

  updateOrderCardPrincipals(
    userId: string,
    code: string,
    principals: string[],
    action: UpdateOrderCardActionType
  ): Observable<OrderCard> {
    const url = this.occEndpointsService.buildUrl('updateAdminOrderCardPrincipals', {
      urlParams: { userId, code },
      queryParams: { action: action },
    });
    return this.httpClient
      .post<Occ.OrderCard>(
        url,
        principals.map((customerId) => ({
          customerId,
        }))
      )
      .pipe(this.converterService.pipeable(ORDER_CARD_NORMALIZER));
  }

  addOrderCardProduct(userId: string, code: string, product: string): Observable<OrderCard> {
    const url = this.occEndpointsService.buildUrl('addOrDeleteAdminOrderCardProduct', {
      urlParams: { userId, code },
      queryParams: { product: product },
    });
    return this.httpClient.put<Occ.OrderCard>(url, {}).pipe(this.converterService.pipeable(ORDER_CARD_NORMALIZER));
  }

  replaceOrderCardProduct(userId: string, code: string, oldProductCode: string, newProductCode: string): Observable<OrderCard> {
    const url = this.occEndpointsService.buildUrl('replaceAdminOrderCardProduct', {
      urlParams: { userId, code, oldProductCode },
      queryParams: { newProductCode },
    });
    return this.httpClient.put<Occ.OrderCard>(url, {}).pipe(this.converterService.pipeable(ORDER_CARD_NORMALIZER));
  }

  addOrderCardArticles(userId: string, code: string, articles: string[]): Observable<OrderCard> {
    const url = this.occEndpointsService.buildUrl('addOrDeleteAdminOrderCardArticles', {
      urlParams: { userId, code },
      queryParams: { articles: articles.join(',') },
    });
    return this.httpClient.patch<Occ.OrderCard>(url, {}).pipe(this.converterService.pipeable(ORDER_CARD_NORMALIZER));
  }

  deleteOrderCardProduct(userId: string, code: string, product: string): Observable<OrderCard> {
    const url = this.occEndpointsService.buildUrl('addOrDeleteAdminOrderCardProduct', {
      urlParams: { userId, code },
      queryParams: { product: product },
    });
    return this.httpClient.delete<Occ.OrderCard>(url, {}).pipe(this.converterService.pipeable(ORDER_CARD_NORMALIZER));
  }

  updateOrderCardProducts(
    userId: string,
    code: string,
    products: string[],
    action: UpdateOrderCardActionType
  ): Observable<OrderCard> {
    const url = this.occEndpointsService.buildUrl('updateAdminOrderCardProducts', {
      urlParams: { userId, code },
      queryParams: { products: products.join(','), action: action },
    });
    return this.httpClient.patch<Occ.OrderCard>(url, {}).pipe(this.converterService.pipeable(ORDER_CARD_NORMALIZER));
  }

  getOrderCardAdminConfigurations(userId: string, code: string, customer: string): Observable<OrderCardConfiguration[]> {
    const url = this.occEndpointsService.buildUrl('orderCardAdminConfigurations', {
      urlParams: { userId, code },
      queryParams: { customer },
    });
    return this.httpClient.get<Occ.OrderCardConfigurationList>(url).pipe(
      map((response) => response.configurations),
      this.converterService.pipeableMany(ORDER_CARD_CONFIGURATION_NORMALIZER)
    );
  }

  getOrderCardAdminConfiguration(
    userId: string,
    code: string,
    product: string,
    customer: string
  ): Observable<OrderCardConfiguration> {
    const url = this.occEndpointsService.buildUrl('orderCardAdminConfiguration', {
      urlParams: { userId, code },
      queryParams: { product, customer },
    });
    return this.httpClient
      .get<Occ.OrderCardConfiguration>(url)
      .pipe(this.converterService.pipeable(ORDER_CARD_CONFIGURATION_NORMALIZER));
  }

  updateOrderCardAdminConfiguration(
    userId: string,
    code: string,
    customer: string,
    configuration: CreateOrUpdateOrderCardConfiguration
  ): Observable<OrderCardConfiguration> {
    const url = this.occEndpointsService.buildUrl('orderCardAdminConfiguration', {
      urlParams: { userId, code },
      queryParams: { customer },
    });
    return this.httpClient
      .put<Occ.OrderCardConfiguration>(url, configuration)
      .pipe(this.converterService.pipeable(ORDER_CARD_CONFIGURATION_NORMALIZER));
  }

  deleteOrderCard(userId: string, code: string): Observable<any> {
    const url = this.occEndpointsService.buildUrl('adminOrderCard', {
      urlParams: { userId, code },
    });
    return this.httpClient.delete(url);
  }
}
